import DiditSdkDefault, { DiditSdk as DiditSdkNamed } from "@didit-protocol/sdk-web";
import KybService from "../../api/services/KybService";
import { buildKybSessionBody, pickKycSessionFields } from "../../utils/kycSessionPayload";
import { alertErrorMessage, alertSuccessMessage } from "../CustomAlertMessage";
import LoaderHelper from "../Loading/LoaderHelper";

/**
 * `POST /api/v1/kyb/session` then open Didit (SDK modal, redirect fallback).
 */
export async function startDiditKybFlow({ userDetails, onFlowEnd, existingDiditUrl, forceNew } = {}) {
  LoaderHelper.loaderStatus(true);
  try {
    try {
      window.dispatchEvent(new CustomEvent("didit:kyb:start"));
    } catch {
      /* ignore */
    }

    let diditUrl =
      forceNew !== true && typeof existingDiditUrl === "string" && existingDiditUrl.length > 0
        ? existingDiditUrl
        : null;

    if (!diditUrl) {
      const body = buildKybSessionBody(userDetails);
      if (forceNew === true) {
        body.forceNew = true;
      }
      const result = await KybService.createSession(body);
      if (result?.success === false || !result) {
        alertErrorMessage(result?.message || "Could not start KYB verification");
        return false;
      }

      diditUrl = pickKycSessionFields(result).diditUrl;
      if (!diditUrl) {
        alertErrorMessage(
          result?.message || "Could not get KYB verification link. Please try again or contact support."
        );
        return false;
      }
      if (result?.message) {
        alertSuccessMessage(result.message);
      }
    }

    // Hide loader before opening Didit modal — loader z-index covers the SDK iframe.
    LoaderHelper.loaderStatus(false);

    try {
      const DiditSdk = DiditSdkNamed || DiditSdkDefault;
      if (!DiditSdk?.shared?.startVerification) {
        throw new Error("Didit SDK failed to load");
      }

      DiditSdk.shared.onComplete = (verificationResult) => {
        DiditSdk.shared.onComplete = undefined;
        try {
          window.dispatchEvent(new CustomEvent("didit:kyb:complete", { detail: verificationResult }));
        } catch {
          /* ignore */
        }
        onFlowEnd?.(verificationResult);
      };

      let finalUrl = diditUrl;
      try {
        const isLight = document.body.classList.contains("light_theme");
        const u = new URL(finalUrl);
        u.searchParams.set("theme", isLight ? "light" : "dark");
        u.searchParams.set("colorMode", isLight ? "light" : "dark");
        finalUrl = u.toString();
      } catch {
        /* ignore */
      }

      await DiditSdk.shared.startVerification({
        url: finalUrl,
        configuration: {
          zIndex: 100050,
          showCloseButton: true,
          showExitConfirmation: true,
          closeModalOnComplete: false,
          loggingEnabled: process.env.NODE_ENV === "development",
        },
      });
      return true;
    } catch (sdkErr) {
      try {
        window.location.assign(diditUrl);
      } catch {
        try {
          const opened = window.open(diditUrl, "_self");
          if (!opened) {
            alertErrorMessage("Could not open KYB verification. Please try again.");
            return false;
          }
        } catch {
          alertErrorMessage("Could not open KYB verification. Please try again.");
          return false;
        }
      }
      onFlowEnd?.({ type: "redirected", url: diditUrl, error: sdkErr?.message || String(sdkErr || "") });
    }
    return true;
  } catch (e) {
    alertErrorMessage(e?.message || "Could not start KYB verification");
    return false;
  } finally {
    try {
      window.dispatchEvent(new CustomEvent("didit:kyb:end"));
    } catch {
      /* ignore */
    }
    LoaderHelper.loaderStatus(false);
  }
}
